import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/24/outline";
import { cx } from "../../utils/cx";

// Up to five page numbers centred on the current page (0-based)
const visiblePages = (current, total) => {
  const count = Math.min(5, total);
  const start = Math.min(Math.max(current - 2, 0), total - count);
  return Array.from({ length: count }, (_, i) => start + i);
};

/**
 * Page controls, 0-based. Pass `totalElements` and `pageSize` to show the
 * "Showing x to y of z" summary on the left.
 */
export default function Pagination({ page, totalPages, onPageChange, totalElements, pageSize, className }) {
  if (!totalPages || totalPages < 1) return null;
  const pages = visiblePages(page, totalPages);
  const last = totalPages - 1;
  const arrow = "rounded-md p-2 text-slate-600 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40";

  return (
    <div className={cx("flex flex-wrap items-center justify-end gap-2 py-4 text-sm", className)}>
      {totalElements > 0 && pageSize && (
        <span className="mr-auto text-slate-500">
          Showing {page * pageSize + 1} to {Math.min((page + 1) * pageSize, totalElements)} of {totalElements.toLocaleString("en-US")} results
        </span>
      )}
      <button type="button" className={arrow} disabled={page === 0} onClick={() => onPageChange(page - 1)} aria-label="Previous page">
        <ChevronLeftIcon className="h-4 w-4" />
      </button>
      {pages.map((p) => (
        <button
          key={p}
          type="button"
          onClick={() => onPageChange(p)}
          aria-current={p === page ? "page" : undefined}
          className={cx("min-w-8 rounded-md px-3 py-1 text-slate-600", p === page ? "bg-brand font-medium text-white" : "hover:bg-slate-100")}
        >
          {p + 1}
        </button>
      ))}
      {pages[pages.length - 1] < last && (
        <>
          <span className="text-slate-400">…</span>
          <button type="button" onClick={() => onPageChange(last)} className="rounded-md px-3 py-1 text-slate-600 hover:bg-slate-100">
            {totalPages}
          </button>
        </>
      )}
      <span className="px-2 text-slate-600">
        Page {page + 1} of {totalPages}
      </span>
      <button type="button" className={arrow} disabled={page >= last} onClick={() => onPageChange(page + 1)} aria-label="Next page">
        <ChevronRightIcon className="h-4 w-4" />
      </button>
    </div>
  );
}
